// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { warnOnce } from '@cloudscape-design/component-toolkit/internal';
import { getVisibleColumnDefinitions } from '../utils';
/**
 * A node in the table header tree.
 * - Column nodes map to column definitions.
 * - Internal nodes map to group definitions.
 * - The root is a virtual container (never rendered).
 */
export class TableHeaderNode {
    constructor(id, props = {}) {
        var _a, _b, _c, _d, _e;
        this.subTreeHeight = 1;
        this.children = [];
        this.id = id;
        this.isRoot = (_a = props.isRoot) !== null && _a !== void 0 ? _a : false;
        this.columnDefinition = props.columnDefinition;
        this.groupDefinition = props.groupDefinition;
        this.colSpan = (_b = props.colSpan) !== null && _b !== void 0 ? _b : 1;
        this.rowSpan = (_c = props.rowSpan) !== null && _c !== void 0 ? _c : 1;
        this.rowIndex = (_d = props.rowIndex) !== null && _d !== void 0 ? _d : -1;
        this.colIndex = (_e = props.colIndex) !== null && _e !== void 0 ? _e : -1;
    }
    get isGroup() {
        return !!this.groupDefinition;
    }
    get isColumn() {
        return !this.isRoot && this.children.length === 0;
    }
    addChild(child) {
        this.children.push(child);
        child.parent = this;
    }
}
/**
 * Builds the tree from the nested columnDisplay structure.
 * Groups are only attached if they contain at least one visible descendant.
 */
function buildTreeFromColumnDisplay(displayItems, columnNodeMap, groupNodeMap, parent) {
    for (const item of displayItems) {
        if (item.type === 'group') {
            const groupNode = groupNodeMap.get(item.id);
            if (!groupNode) {
                warnOnce('[Table]', `Group "${item.id}" referenced in columnDisplay not found in groupDefinitions. Skipping.`);
                continue;
            }
            buildTreeFromColumnDisplay(item.children, columnNodeMap, groupNodeMap, groupNode);
            // Only attach group if it has visible descendants. The recursive call above
            // only adds children that are either visible columns or nested groups with
            // their own visible descendants, so this check handles all nesting levels.
            if (groupNode.children.length > 0) {
                parent.addChild(groupNode);
            }
        }
        else {
            if (!item.visible) {
                continue;
            }
            const colNode = columnNodeMap.get(item.id);
            if (colNode) {
                parent.addChild(colNode);
            }
        }
    }
}
/**
 * Fallback when no columnDisplay is provided: all visible columns attach directly to root.
 */
function buildTreeFromVisibleColumns(visibleColumns, columnNodeMap, root) {
    for (const col of visibleColumns) {
        // Columns without IDs cannot participate in grouping, they have no key
        // to match against columnDisplay entries or groupDefinitions.
        if (!col.id) {
            continue;
        }
        const node = columnNodeMap.get(col.id);
        if (node) {
            root.addChild(node);
        }
    }
}
function computeSubTreeHeights(node) {
    if (node.isColumn || node.children.length === 0) {
        node.subTreeHeight = 1;
        return 1;
    }
    const maxChildHeight = Math.max(...node.children.map(child => computeSubTreeHeights(child)));
    node.subTreeHeight = maxChildHeight + 1;
    return node.subTreeHeight;
}
function computeRowSpansAndIndices(node, treeHeight, ancestorRows = 0) {
    const maxChildHeight = Math.max(...node.children.map(c => c.subTreeHeight), 0);
    node.rowSpan = treeHeight - ancestorRows - maxChildHeight;
    if (node.parent) {
        node.rowIndex = node.parent.rowIndex + node.parent.rowSpan;
    }
    for (const child of node.children) {
        computeRowSpansAndIndices(child, treeHeight, ancestorRows + node.rowSpan);
    }
}
function computeColSpansAndIndices(node, startCol = 0) {
    node.colIndex = startCol;
    if (node.isColumn) {
        node.colSpan = 1;
        return startCol + 1;
    }
    let nextCol = startCol;
    for (const child of node.children) {
        nextCol = computeColSpansAndIndices(child, nextCol);
    }
    node.colSpan = nextCol - startCol;
    return nextCol;
}
export function calculateHierarchyTree(columnDefinitions, visibleColumnIds, groupDefinitions, columnDisplay) {
    const visibleColumns = getVisibleColumnDefinitions({
        columnDisplay,
        visibleColumns: visibleColumnIds,
        columnDefinitions,
    });
    const columnNodeMap = new Map();
    const groupNodeMap = new Map();
    for (const col of visibleColumns) {
        if (col.id) {
            columnNodeMap.set(col.id, new TableHeaderNode(col.id, { columnDefinition: col }));
        }
    }
    for (const group of groupDefinitions) {
        groupNodeMap.set(group.id, new TableHeaderNode(group.id, { groupDefinition: group }));
    }
    const root = new TableHeaderNode('*', { isRoot: true });
    if (columnDisplay && columnDisplay.length > 0) {
        buildTreeFromColumnDisplay(columnDisplay, columnNodeMap, groupNodeMap, root);
    }
    else {
        buildTreeFromVisibleColumns(visibleColumns, columnNodeMap, root);
    }
    computeSubTreeHeights(root);
    const treeHeight = root.subTreeHeight - 1;
    root.rowIndex = -1;
    root.rowSpan = 1;
    root.colSpan = visibleColumns.length;
    for (const child of root.children) {
        computeRowSpansAndIndices(child, treeHeight);
    }
    computeColSpansAndIndices(root);
    return buildOutput(root, treeHeight);
}
function getParentChain(node) {
    const chain = [];
    let current = node.parent;
    while (current && !current.isRoot) {
        chain.unshift(current.id);
        current = current.parent;
    }
    return chain;
}
function buildOutput(root, maxDepth) {
    var _a, _b, _c;
    const rowsMap = new Map();
    const columnToParentIds = new Map();
    const queue = [...root.children];
    while (queue.length > 0) {
        const node = queue.shift();
        const parentChain = getParentChain(node);
        const entry = {
            id: node.id,
            cellKey: node.isGroup ? `group:${node.id}` : `column:${node.id}`,
            header: (_b = (_a = node.groupDefinition) === null || _a === void 0 ? void 0 : _a.header) !== null && _b !== void 0 ? _b : (_c = node.columnDefinition) === null || _c === void 0 ? void 0 : _c.header,
            colSpan: node.colSpan,
            rowSpan: node.rowSpan,
            isGroup: node.isGroup,
            columnDefinition: node.columnDefinition,
            groupDefinition: node.groupDefinition,
            parentGroupIds: parentChain,
            colIndex: node.colIndex,
        };
        if (!rowsMap.has(node.rowIndex)) {
            rowsMap.set(node.rowIndex, []);
        }
        rowsMap.get(node.rowIndex).push(entry);
        if (node.isColumn && node.columnDefinition && parentChain.length > 0) {
            columnToParentIds.set(node.id, parentChain);
        }
        queue.push(...node.children);
    }
    // Sort row indices to ensure rows are ordered top-to-bottom,
    // then sort columns within each row by their horizontal position.
    const rows = Array.from(rowsMap.keys())
        .sort((a, b) => a - b)
        .map(key => ({ columns: rowsMap.get(key).sort((a, b) => a.colIndex - b.colIndex) }));
    return { rows, maxDepth, columnToParentIds };
}
export function getColumnGroupsDepth(columnDisplay) {
    if (!columnDisplay) {
        return 0;
    }
    let maxDepth = 0;
    for (const item of columnDisplay) {
        if (item.type === 'group') {
            maxDepth = Math.max(maxDepth, 1 + getColumnGroupsDepth(item.children));
        }
    }
    return maxDepth;
}
//# sourceMappingURL=utils.js.map